import { useState } from "react"
import { FormField, Text, Textarea } from "@smooth/design-system-components"
import {
  aiOriginalExemplars,
  expectationJson,
  modelExemplars,
  parseExpectationJson,
  withoutExemplars,
} from "../expectation"
import type { Expectation, ExpectationItem } from "../expectation"
import { Bubble } from "./Conversation"
import { TerminalBadge } from "./parts"

function ItemList({ title, items }: { title: string; items: ExpectationItem[] }) {
  if (!items.length) return null
  return (
    <div className="flex flex-col gap-1">
      <Text size="sm" weight="bold">
        {title}
      </Text>
      <ul className="list-disc pl-6 text-body text-text-default">
        {items.map((item) => (
          <li key={item.id}>{item.label ?? item.id}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * 期待の形（ほかに許す対応・必ず入れること・禁止表現）の表示。AI には渡さない。
 * お手本の文は生成結果と見比べられるよう、生成結果の側（ExemplarReference）に出す。
 * 期待の形を持たない従来のケースは何も出さない（期待する対応だけ）
 */
export function ExpectationSummary({
  expectation,
  expectedTerminal,
}: {
  expectation?: Expectation | null
  expectedTerminal: string
}) {
  if (!expectation) return null
  const others = (expectation.strategy?.terminals ?? []).filter((id) => id !== expectedTerminal)
  return (
    <div aria-label="期待の形" className="flex flex-col gap-2">
      {others.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-2">
          <Text as="span" size="sm" weight="bold">
            ほかに許す対応
          </Text>
          {others.map((id) => (
            <TerminalBadge key={id} id={id} />
          ))}
        </div>
      )}
      <ItemList title="必ず入れること" items={expectation.required ?? []} />
      <ItemList title="禁止表現" items={expectation.forbidden ?? []} />
    </div>
  )
}

export function ExemplarReference({
  expectation,
  editing = false,
}: {
  expectation?: Expectation | null
  /** 編集中は当時の AI の下書きを出さない（編集の対象ではないので） */
  editing?: boolean
}) {
  const models = modelExemplars(expectation)
  const originals = editing ? [] : aiOriginalExemplars(expectation)
  if (!models.length && !originals.length) return null
  return (
    <div aria-label="オペレーターが送った文" className="flex min-w-0 flex-col gap-3">
      {models.map((exemplar, index) => (
        <Bubble
          key={index}
          direction="outbound"
          meta={exemplar.source === "operator_sent" ? "オペレーターが送った文" : "お手本の文"}
        >
          {exemplar.body}
        </Bubble>
      ))}
      {originals.length > 0 && (
        <details className="border-t border-border-default">
          <summary className="cursor-pointer py-2 text-body font-bold text-text-default">
            当時の AI の下書き
          </summary>
          <div className="flex flex-col gap-3 pb-2">
            {originals.map((exemplar, index) => (
              <Bubble key={index} direction="outbound" meta="当時の AI の下書き" draft>
                {exemplar.body}
              </Bubble>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

/**
 * 期待の形を JSON で編集する欄（最小限の編集手段）。JSON として読めるときだけ変更を反映し、読めない間は理由を出す。
 * 模範文はお手本の文の欄で直すので、ここには出さず、保存済みのものをそのまま引き継ぐ。
 * 許す対応の先頭は期待する対応に揃えて保存される
 */
export function ExpectationJsonField({
  expectation,
  expectedTerminal,
  onChange,
}: {
  expectation?: Expectation | null
  expectedTerminal: string
  onChange: (expectation: Expectation) => void
}) {
  const [text, setText] = useState(() =>
    expectationJson(expectation && withoutExemplars(expectation), expectedTerminal)
  )
  const [error, setError] = useState<string | null>(null)
  return (
    <div className="flex flex-col gap-2">
      <FormField label="期待の形（JSON）" error={error ?? undefined}>
        <Textarea
          rows={10}
          value={text}
          error={!!error}
          onChange={(e) => {
            setText(e.target.value)
            const parsed = parseExpectationJson(e.target.value)
            setError(parsed.error)
            if (!parsed.value) return
            const exemplars = expectation?.exemplars
            onChange(
              exemplars?.length && !parsed.value.exemplars
                ? { ...parsed.value, exemplars }
                : parsed.value
            )
          }}
        />
      </FormField>
    </div>
  )
}
